// ======================================================================
// File Name       : CourseHistoryDetail
//
// Copyright(C) 2017 NIPPON TELEGRAPH AND TELEPHONE EAST CORPORATION All
// Rights Reserved.
//
// This source code or any portion thereof must not be
// reproduced or used in any manner whatsoever.
// ======================================================================

import React from 'react';
import { Button, Col, Form, Table } from 'react-bootstrap';
import { Bar, BarChart, CartesianGrid, Cell, LabelList, Legend, Pie, PieChart, ResponsiveContainer, XAxis, YAxis, Surface, Symbols } from 'recharts';
import { connect } from 'react-redux';
import BackButton from '../../../components/BackButton';
import * as UserConstants from '../../../constants/UserConstants';
import { parseDateForDetail } from '../../../utils/index';
import { getCourseStatus } from '../../../utils/UserUtils';
import PaginationCustom from '../../Pagination';
import TableHead, { setSortState } from '../../TableHead';
import * as CourseConstants from '../../../constants/CourseConstants';
import { DORMANT_STATE, HYPHEN } from '../../../constants';
import * as ICON from '../../../constants/IconConstants';
import { USER_HISTORY_COURSE_STUDENT_DETAIL } from '../../../constants/PathConstants';
import CustomLink from '../../button/CustomLink';

class CourseHistoryDetail extends React.Component {
  render() {
    const {
      courseDetail,
      getStartEndData,
      handleSortState,
      startPosition,
      onGoToListScreen,
      userInfo,
      userGroupBySecernId,
      structures,
      goToDetail,
      dataSort,
      onRegisterSendMail,
      pieChartData,
      barChartData,
      handleMouseEnter,
      handleMouseLeave,
      hoverIndex,
      managementFunctionAuthorityInfo, // STEP13.0.0 TAS-22571
    } = this.props;

    const thead = [
      { title: 'ユーザー名', sortBy: null },
      { title: 'ユーザーグループ名', sortBy: null },
      { title: 'コース進捗率', sortBy: 4, width:'10%' },
      { title: 'コース完了ステータス', sortBy: 2 },
      { title: 'コース完了日時', sortBy: 3 },
      { title: '操作', sortBy: null, width: 130 },
    ];
    let data = thead;
    if (dataSort) {
      const sortBy = dataSort.courseStatusSortType;
      const sortState = dataSort.ascendingDescendingSort;
      data = setSortState(thead, sortBy, sortState);
    }
    const style = {
      background: 'none',
      width: 'auto',
      marginLeft: 0,
      left: 0,
    };
    let historyDetailTableData = null;
    let numberOfCompleteUsers = 0;
    // #7436 START
    const isNotSetEndConditionTypeForCourse = courseDetail.endConditionTypeForCourse === CourseConstants.END_CONDITION_TYPE_FOR_COURSE.NONE;
    // #7436 END

    if (courseDetail.userList) {
      historyDetailTableData = courseDetail.userList.map((historyDetailData, index) => {
        const isDormantState = userInfo.bySecernId[historyDetailData.userSecernId] && userInfo.bySecernId[historyDetailData.userSecernId].dormantState === DORMANT_STATE.INACTIVE;
        const cssClass = isDormantState ? 'inactive-class' : '';
        const userName = userInfo.bySecernId[historyDetailData.userSecernId] ? userInfo.bySecernId[historyDetailData.userSecernId].userName : '';
        let groupName = '';
        if (userGroupBySecernId[historyDetailData.userSecernId] && userGroupBySecernId[historyDetailData.userSecernId].groupSecernId) {
          const listLength = userGroupBySecernId[historyDetailData.userSecernId].groupSecernId.length;
          for (let i = 0; i < listLength; i++) {
            const id = userGroupBySecernId[historyDetailData.userSecernId].groupSecernId[i];
            if (structures.bySecernId[id]) {
              if (groupName !== '') groupName += ', ';
              groupName += structures.bySecernId[id].groupName;
            }
          }
        }
        if (historyDetailData.courseStatus === UserConstants.COURSE_COMPLETITION_STATUS_ID.DONE) {
          numberOfCompleteUsers++;
        }
        // #7436 START
        const status = (isNotSetEndConditionTypeForCourse || isDormantState) ? HYPHEN : getCourseStatus(historyDetailData.courseStatus); // Step 10.1.0
        const endConditionAchievementDate = isNotSetEndConditionTypeForCourse? HYPHEN : parseDateForDetail(historyDetailData.endConditionAchievementDate);
        // #7436 END
        // Step 10.1.0 START
        // Step 13.0.0 TAS-23169 START
        const courseProgressRatio = isDormantState ? HYPHEN : `${historyDetailData.courseProgressRatio ?? 0} %`;
        // Step 13.0.0 TAS-23169 END
        // Step 10.1.0 END

        return (
          <tr key={index} className={cssClass} onMouseEnter={() => handleMouseEnter(index)} onMouseLeave={() => handleMouseLeave()}>
            <td>{userName}</td>
            <td>{groupName}</td>
            <td>{courseProgressRatio}</td>
            <td>{status}</td>
            <td>{endConditionAchievementDate}</td>
            <td className="text-center">
              <CustomLink
                className="margin-right-7 link-detail"
                onClick={() => {
                  goToDetail(historyDetailData.userSecernId);
                }}
                text="詳細を見る"
              />
            </td>
          </tr>
        );
      });
    }
    const isDisableSendEmail = (courseDetail.userList && (courseDetail.userList.length === numberOfCompleteUsers)) ||
                  (parseInt(courseDetail.endConditionTypeForCourse, 10) === CourseConstants.END_CONDITION_TYPE_FOR_COURSE.NONE); // #6759
    const isShowButtonSendEmail = managementFunctionAuthorityInfo.emailManagementAuthority === 2; // STEP13.0.0 TAS-22571
    // #7386 START
    // Check show empty pieChart or not
    let isShowEmptyPieChart = false;
    let { notImplementedUserCount } = pieChartData;
    const { endConditionAchievementUserCount, inProgressUserCount } = pieChartData;
    if (!endConditionAchievementUserCount && !inProgressUserCount && !notImplementedUserCount) {
      notImplementedUserCount = 1; // need set value to display pieChart
      isShowEmptyPieChart = true;
    }

    // PieChart data
    let pieChartDataForRender;
    if (isNotSetEndConditionTypeForCourse) {
      pieChartDataForRender = [
        { name: '実施済', value: inProgressUserCount },
        { name: '未実施', value: notImplementedUserCount },
      ];
    } else {
      pieChartDataForRender = [
        { name: '完了', value: endConditionAchievementUserCount },
        { name: '実施中', value: inProgressUserCount },
        { name: '未実施', value: notImplementedUserCount },
      ];
    }

    // BarChart data
    const barChartDataForRender = [];
    Object.keys(barChartData).map((index) => {
      const cur = barChartData[index];
      barChartDataForRender.push({ name: cur.completionDay, value: cur.courseCompletionUserCount });
    });

    // #7436 START
    const COLORS = isNotSetEndConditionTypeForCourse ? ['#99FF99', '#F2F2F2'] : ['#99FF99', '#FFC000', '#F2F2F2'];
    // #7436 END
    const RADIAN = Math.PI / 180;

    // Customize Pie Label
    const renderCustomizedPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
      const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
      const x = cx + radius * Math.cos(-midAngle * RADIAN);
      const y = cy + radius * Math.sin(-midAngle * RADIAN);
      if (value > 0) {
        return (
          <text
            x={x}
            y={y}
            fill="#000000"
            textAnchor="middle"
            dominantBaseline="central"
          >
            {value}
          </text>
        );
      }
      return <text />;
    };

    // Customize Bar Label
    const renderCustomBarLabel = (props) => {
      const {
        value,
        viewBox,
      } = props;
      const { x, y, width, height } = viewBox;
      if (value > 0) {
        return (
          <text
            textAnchor="middle"
            x={x + width / 2}
            y={y + height / 2}
            width={width}
            height={height}
            fill="#000000"
            dominantBaseline="central"
          >
            {value}
          </text>
        );
      }
      return <text />;
    };

    // Customize Pie Legend
    const renderCustomizedPieLegend = ({ payload }) => {
      return (
        <div>
          {payload.map((entry, index) => {
            const { dataKey, data, color } = entry;
            return (
              <div key={dataKey} className='margin-top-bot-10'>
                <svg width={50} height={15}>
                  <rect cx={5} cy={5} width={50} height={15} fill={color} />
                </svg>
                <span className="margin-left-10" key={dataKey}>{data}</span>
              </div>
            );
          })}
        </div>
      );
    };
    // #7386 END

    return (
      <div>
        <Col sm={12} className="padding-right-clear">
          <Form className='form-horizontal'>
            <Form.Group className="form-info padding-right-clear padding-left-clear">
              <Col as={Form.Label} sm={5} className="padding-right-clear">コース名</Col>
              <Col sm={7} className="padding-top-bottom-10">{courseDetail.courseName}</Col>
            </Form.Group>
            <Form.Group className="form-info padding-right-clear padding-left-clear">
              <Col as={Form.Label} sm={5} className="padding-right-clear">
                コース完了状況
              </Col>
              <Col sm={6} className="padding-top-bottom-10">
                {Object.keys(courseDetail).length > 0 && isNotSetEndConditionTypeForCourse && (
                  HYPHEN
                )}

                {Object.keys(courseDetail).length > 0 && !isNotSetEndConditionTypeForCourse && (
                  `${courseDetail.courseCompletitionStatus} 人`
                )}
              </Col>
              <Col sm={12} as={Form.Label}>
                ※休止状態のユーザーを含む
              </Col>
              {/* #7386 START */}
              <Col sm={12} className="padding-left-right-clear">
                <Col sm={{ span: 7, offset: 5 }} className="padding-left-right-clear">
                  {Object.keys(courseDetail).length > 0 && (
                    <ResponsiveContainer height={200} width={'100%'}>
                      <PieChart>
                        <Pie
                          data={pieChartDataForRender}
                          cx="15%"
                          cy="50%"
                          outerRadius={65}
                          labelLine={false}
                          label={isShowEmptyPieChart ? '' : renderCustomizedPieLabel}
                          fill="#8884d8"
                          dataKey="value"
                          isAnimationActive={false}
                          startAngle={isShowEmptyPieChart ? undefined : 90}
                          endAngle={isShowEmptyPieChart ? undefined : -270}
                        >
                          {pieChartDataForRender.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={isShowEmptyPieChart ? '#F2F2F2' : COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                        {
                          !isShowEmptyPieChart && (
                            <Legend
                              layout="vertical"
                              verticalAlign="middle"
                              wrapperStyle={{
                                top: 0,
                                left: '300px',
                                right: 0,
                                height: '200px',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                              payload={
                                isNotSetEndConditionTypeForCourse
                                  ? [
                                    { data: '実施済', dataKey: '実施済', color: '#99FF99' },
                                    { data: '未実施', dataKey: '未実施', color: '#F2F2F2' },
                                  ]
                                  : [
                                    { data: '完了', dataKey: '完了', color: '#99FF99' },
                                    { data: '実施中', dataKey: '実施中', color: '#FFC000' },
                                    { data: '未実施', dataKey: '未実施', color: '#F2F2F2' },
                                  ]
                              }
                              content={renderCustomizedPieLegend}
                            />
                          )
                        }
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </Col>
              </Col>
            </Form.Group>
            <Form.Group className="form-info padding-right-clear padding-left-clear">
              <Col as={Form.Label} sm={5}>
                コース完了人数(日）
              </Col>
              {Object.keys(courseDetail).length > 0 && isNotSetEndConditionTypeForCourse && (
                <Col sm={7} className="padding-top-bottom-10">
                  {HYPHEN}
                </Col>
              )}

              {Object.keys(courseDetail).length > 0 && !isNotSetEndConditionTypeForCourse && (
                <Col sm={7} className="padding-top-bottom-10">
                  <Col sm={12} className="padding-left-right-clear" style={{ marginLeft: '-50px' }}>
                    <ResponsiveContainer height={350} width={'95%'}>
                      <BarChart
                        data={barChartDataForRender}
                        margin={{ left: 5, right: 5 }}
                        maxBarSize={10} // maximum size of bar
                        height={350}
                      >
                        <CartesianGrid strokeDasharray="3" vertical={false} />
                        <XAxis
                          dataKey="name"
                          angle={-90} // display text in vertical
                          textAnchor="end"
                          interval={0} // display all stick
                          height={150}
                        />
                        {/* // #7463 START */}
                        <YAxis
                          allowDecimals={false}
                        />
                        {/* // #7463 END */}
                        <Bar dataKey="value" fill="#99FF99" stackId="value" isAnimationActive={false}>
                          <LabelList
                            dataKey="value"
                            content={(props) => renderCustomBarLabel(props, "value")}
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Col>
                  {/* #7435 START */}
                  <Col sm={12} className='padding-right-15'>
                    <Button
                      variant="default"
                      className="float-end margin-left-10"
                      onClick={this.props.getNextMonthBarChart}
                    >
                      {'翌月 ＞'}
                    </Button>
                    <Button
                      variant="default"
                      className="float-end"
                      onClick={this.props.getPreviousMonthBarChart}
                    >
                      {'＜ 前月'}
                    </Button>
                  </Col>
                  {/* #7435 END */}
                </Col>
              )}
            </Form.Group>
            {/* #7386 END */}
            <Form.Group className="form-info padding-left-right-clear">
              <Col as={Form.Label} sm={5} className="padding-right-clear">
                コンテンツ総数
              </Col>
              <Col sm={7} className="padding-top-bottom-10">
                {courseDetail.totalItem}
              </Col>
              <Col sm={12} as={Form.Label}>
                ※ 完了条件が設定されているコンテンツの総数
              </Col>
            </Form.Group>
            {/* STEP13.0.0 TAS-22571 START */}
            {isShowButtonSendEmail && ( 
              <Form.Group>
                <Col sm={12} className="padding-left-right-clear margin-bottom-15">
                  <Col sm={3} className="padding-left-clear">
                    <Button
                      variant="primary"
                      className="float-start margin-top-20"
                      onClick={onRegisterSendMail}
                      disabled={isDisableSendEmail}
                    >
                      未完了ユーザーへメールを送信
                    </Button>
                  </Col>
                </Col>
              </Form.Group>
            )}
            {/* STEP13.0.0 TAS-22571 END */}
          </Form>
        </Col>
        <Table striped hover className="margin-top-10">
          <thead>
            <TableHead data={data} handleSortState={handleSortState.bind(this)} />
          </thead>
          <tbody>
            {historyDetailTableData}
          </tbody>
        </Table>
        <Form.Group>
          <Col sm={12} className="d-flex justify-content-center">
            <PaginationCustom
              recordNum={courseDetail.recordNum}
              handlePageSelect={getStartEndData}
              startPosition={startPosition}
            />
          </Col>
        </Form.Group>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const { structures } = state.group;
  const { courseDetail, userGroupBySecernId, pieChartData, barChartData } = state.userHistoryCourse;
  const { userInfo } = state.user;
  return {
    ...{ structures },
    ...{ courseDetail },
    ...{ userGroupBySecernId },
    ...{ userInfo },
    ...{ pieChartData },
    ...{ barChartData },
  };
};

export default connect(mapStateToProps)(CourseHistoryDetail);
